import React, { useState } from 'react';
import { X, Calendar, ChevronDown, Info } from 'lucide-react';

const TIME_RANGES = [
  { id: '24m', label: 'Last 24 months' },
  { id: '12m', label: 'Last 12 months' },
  { id: '6m', label: 'Last 6 months' },
  { id: '1m', label: 'Last 1 month' },
  { id: '7d', label: 'Last 7 days' },
  { id: 'custom', label: 'Custom' },
];
const GRANULARITY = [
  { id: 'hourly', label: 'Hourly' },
  { id: 'daily', label: 'Daily' },
  { id: 'weekly', label: 'Weekly' },
  { id: 'monthly', label: 'Monthly' },
  { id: 'yearly', label: 'Yearly' },
  { id: 'automatic', label: 'Automatic' },
];
const COUNTRIES = [
  { value: '', label: 'Select' },
  { value: 'us', label: 'United States' },
  { value: 'in', label: 'India' },
  { value: 'uk', label: 'United Kingdom' },
  { value: 'ca', label: 'Canada' },
  { value: 'de', label: 'Germany' },
  { value: 'br', label: 'Brazil' },
];
const INITIAL_STATE = {
  timeRange: 'custom',
  granularity: 'automatic',
  from: '',
  to: '',
  jobOwner: '',
  jobs: '',
  country: '',
};

function Radio({ checked, onChange, label, hasInfo }) {
  return (
    <label className="inline-flex items-center gap-2 cursor-pointer select-none py-1.5">
      <span
        className={`w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 ${
          checked ? 'border-[#5B4FE8]' : 'border-[#C4BEDA]'
        }`}
      >
        {checked && <span className="w-2 h-2 rounded-full bg-[#5B4FE8]" />}
      </span>
      <input type="radio" checked={checked} onChange={onChange} className="sr-only" />
      <span className="text-[13.5px] text-[#1A1533]">{label}</span>
      {hasInfo && <Info size={11} className="text-[#A39DB8]" />}
    </label>
  );
}

function RadioList({ items, value, onSelect, hasInfoOn }) {
  return (
    <div className="space-y-0.5">
      {items.map((r) => (
        <div key={r.id}>
          <Radio
            checked={value === r.id}
            onChange={() => onSelect(r.id)}
            label={r.label}
            hasInfo={hasInfoOn === r.id}
          />
        </div>
      ))}
    </div>
  );
}

function DateField({ label, value, onChange }) {
  return (
    <div>
      <label className="text-[12.5px] text-[#4A4463]">{label}</label>
      <div className="relative mt-1.5">
        <input
          type="date"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full pl-3 pr-10 py-2.5 rounded-xl border border-[#ECE6FA] bg-white text-[13px] outline-none focus:ring-2 focus:ring-[#5B4FE8]/30"
        />
        <Calendar size={15} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#5B4FE8] pointer-events-none" />
      </div>
    </div>
  );
}

function DateRange({ from, to, setFrom, setTo }) {
  return (
    <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
      <DateField label="Date (from)" value={from} onChange={setFrom} />
      <DateField label="Date (to)" value={to} onChange={setTo} />
    </div>
  );
}

function TextFilter({ label, value, onChange, placeholder }) {
  return (
    <section className="rounded-2xl bg-white border border-[#ECE6FA] p-5">
      <p className="text-[13px] font-bold text-[#1A1533] mb-2">{label}</p>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full px-3 py-2.5 rounded-xl border border-[#ECE6FA] bg-[#FAF8FF] text-[13px] outline-none focus:ring-2 focus:ring-[#5B4FE8]/30"
      />
    </section>
  );
}

function CountryFilter({ value, onChange }) {
  return (
    <section className="rounded-2xl bg-white border border-[#ECE6FA] p-5">
      <p className="text-[13px] font-bold text-[#1A1533] mb-2">By country</p>
      <div className="relative">
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full appearance-none px-3 py-2.5 rounded-xl border border-[#ECE6FA] bg-[#FAF8FF] text-[13px] outline-none focus:ring-2 focus:ring-[#5B4FE8]/30 text-[#1A1533]"
        >
          {COUNTRIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
        </select>
        <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#5B4FE8] pointer-events-none" />
      </div>
    </section>
  );
}

function TimeSection({ state, set }) {
  return (
    <section className="rounded-2xl bg-white border border-[#ECE6FA] p-5">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6">
        <div>
          <p className="text-[13px] font-bold text-[#1A1533] mb-2">By time</p>
          <RadioList items={TIME_RANGES} value={state.timeRange} onSelect={set('timeRange')} />
        </div>
        <div>
          <p className="text-[13px] font-bold text-[#1A1533] mb-2">Time granularity</p>
          <RadioList items={GRANULARITY} value={state.granularity} onSelect={set('granularity')} hasInfoOn="automatic" />
        </div>
      </div>
      <DateRange from={state.from} to={state.to} setFrom={set('from')} setTo={set('to')} />
    </section>
  );
}

function DrawerHeader({ onClose }) {
  return (
    <div className="flex items-center justify-between px-6 py-5 bg-white border-b border-[#ECE6FA]">
      <h3 className="text-[18px] font-bold text-[#1A1533]">Filters</h3>
      <button
        onClick={onClose}
        className="w-8 h-8 rounded-full hover:bg-[#F1ECFE] flex items-center justify-center text-[#4A4463]"
      >
        <X size={16} />
      </button>
    </div>
  );
}

function DrawerFooter({ onReset, onApply }) {
  return (
    <div className="px-6 py-4 bg-white border-t border-[#ECE6FA] flex items-center justify-end gap-3">
      <button
        onClick={onReset}
        className="px-5 py-2.5 rounded-full bg-[#F1ECFE] text-[#1A1533] text-[13px] font-semibold hover:bg-[#E9E3FB]"
      >
        Reset
      </button>
      <button
        onClick={onApply}
        className="px-5 py-2.5 rounded-full bg-[#5B4FE8] text-white text-[13px] font-semibold hover:bg-[#4A3FD9] shadow-md"
      >
        Apply
      </button>
    </div>
  );
}

export default function FiltersDrawer({ open, onOpenChange }) {
  const [state, setState] = useState(INITIAL_STATE);
  const set = (key) => (v) => setState((s) => ({ ...s, [key]: v }));
  const reset = () => setState(INITIAL_STATE);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-[#1A1533]/35" onClick={() => onOpenChange(false)} />
      <div className="absolute right-0 top-0 h-full w-full sm:max-w-[520px] bg-[#F7F5FF] shadow-2xl flex flex-col">
        <DrawerHeader onClose={() => onOpenChange(false)} />

        <div className="flex-1 overflow-y-auto zara-scroll p-5 space-y-5">
          <TimeSection state={state} set={set} />
          <TextFilter label="By job owner" value={state.jobOwner} onChange={set('jobOwner')} placeholder="Enter name" />
          <TextFilter label="By jobs" value={state.jobs} onChange={set('jobs')} placeholder="Enter job name" />
          <CountryFilter value={state.country} onChange={set('country')} />
        </div>

        <DrawerFooter onReset={reset} onApply={() => onOpenChange(false)} />
      </div>
    </div>
  );
}
